/**
 * Centralized API Service
 *
 * Provides a singleton API client instance. Auth is cookie-based (httpOnly
 * PHX-1180 cookie, sent via withCredentials) — no Authorization header is
 * attached. Also handles response/error handling and 401 refresh-token retry.
 */
import type { AxiosInstance } from 'axios';
import { createApiClient } from '@tms-phoenix/ui-vue';
import { config } from '@/core/config';
import { areKeyMarkersEnabled, deepStripKeyMarkers } from '@/shared/utils/key-markers';

let apiInstance: AxiosInstance | null = null;

/**
 * Get the singleton API client instance
 * Automatically includes auth token from auth store when available
 */
export function getApiClient(): AxiosInstance {
  if (!apiInstance) {
    apiInstance = createApiClient({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      // ── 401 token-refresh callback ─────────────────────────────────────
      // Called by createApiClient when any authenticated request returns 401.
      // Lazy-imports auth store to break the circular dependency
      // (auth.store → api.service → auth.store).
      // The store's refreshAccessToken() handles clearAuth() + /login redirect
      // on failure, so we only need to return the new token or null here.
      onRefreshToken: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        const authStore = useAuthStore();
        const success = await authStore.refreshAccessToken();
        return success ? authStore.accessToken : null;
      },
      // SV-563: a 401 from /auth/session-check is handled by authStore.validateSession() itself.
      skipRefreshForUrls: ['/auth/login', '/auth/logout', '/auth/refresh', '/auth/session-check'],
      // A refreshed token that still 401s means the session is invalid server-side —
      // force a client-side logout instead of retrying refresh again.
      onAuthFailure: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        useAuthStore().forceLogout();
      },
    });

    // ── Tenant hostname header ───────────────────────────────────────────
    // Send x-tenant-hostname on every request so backend services can
    // resolve the tenant context (Cognito pool, DB schema, etc.).
    apiInstance.interceptors.request.use((requestConfig) => {
      if (requestConfig.headers) {
        requestConfig.headers['x-tenant-hostname'] = window.location.hostname;
      }
      return requestConfig;
    });

    // ── Key-inspector marker guard ───────────────────────────────────────
    // While the translation key inspector is active, `t()` output carries an
    // invisible marker suffix (see `shared/utils/key-markers.ts`). A field
    // whose value defaulted from a marked `t()` call — a prefilled note, a
    // dropdown's translated label sent back as a value — would otherwise
    // ship that marker to the backend and into stored data. Cheap to check
    // unconditionally: `areKeyMarkersEnabled()` is a plain boolean read, and
    // `deepStripKeyMarkers` only runs when it's true.
    apiInstance.interceptors.request.use((requestConfig) => {
      if (areKeyMarkersEnabled()) {
        if (requestConfig.data) requestConfig.data = deepStripKeyMarkers(requestConfig.data);
        if (requestConfig.params) requestConfig.params = deepStripKeyMarkers(requestConfig.params);
      }
      return requestConfig;
    });
  }

  return apiInstance;
}

/**
 * Reset the API client instance (useful for testing or auth changes)
 */
export function resetApiClient(): void {
  apiInstance = null;
}
